import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, CheckCircle, Info, Edit3, Clock, Eye, AlertTriangle, Rocket, X, Wand2, Link2, Globe } from 'lucide-react';
import { auth } from '../../config/firebase';
import { useTranslation } from 'react-i18next';
import type { SocialPlatform } from '../icons/SocialIcon';
import { UTMModal } from '../modals';
import { CreateSmartLinkModal } from '../modals/CreateSmartLinkModal';
import { useCreateLink } from '../../hooks/useLinks';
import { useBrands } from '../../hooks/useBrands';
import type { Brand } from '../../types';

interface PostContentCardProps {
  content: string;
  onContentChange: (content: string) => void;
  // Keep media props for compatibility but won't be used
  media?: File | null;
  onClearMedia?: () => void;
  onMediaChange?: (file: File | null) => void;
  existingMediaUrl?: string | null;
  existingMediaType?: 'image' | 'video' | null;
  status?: string;
  // Keep format props for compatibility but won't be used
  format?: 'regular' | 'reel';
  onFormatChange?: (format: 'regular' | 'reel') => void;
  brandId?: string;
  selectedPlatforms: SocialPlatform[];
  aiResponse?: AiBoostResponse | null;
  onAiResponseChange?: (response: AiBoostResponse | null) => void;
}

interface AiBoostResponse {
  hookStrength: number;
  brandAlignment: number;
  ctaEffectiveness: number;
  platformOptimization: number;
  contentQuality: number;
  overallScore: number;
  originalScore: {
    hookStrength: number;
    brandAlignment: number;
    ctaEffectiveness: number;
    platformOptimization: number;
    contentQuality: number;
    overallScore: number;
  };
  feedback: string;
  enhancedContent: string;
  enhancedScores: {
    hookStrength: number;
    brandAlignment: number;
    ctaEffectiveness: number;
    platformOptimization: number;
    contentQuality: number;
    overallScore: number;
  };
  reasoning: string;
  originalContent: string;
  brandId: string;
  platforms: string[];
  language: string;
  timestamp: string;
}

export const PostContentCard: React.FC<PostContentCardProps> = ({
  content: initialContent,
  onContentChange,
  // Media and format props kept for compatibility
  media,
  onMediaChange,
  onClearMedia,
  existingMediaUrl,
  existingMediaType,
  status = 'draft',
  format,
  onFormatChange,
  brandId = '',
  selectedPlatforms = [],
  aiResponse,
  onAiResponseChange
}) => {
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState(initialContent);
  const [isUrlDetected, setIsUrlDetected] = useState(false);
  const [extractedUrl, setExtractedUrl] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [showBoostTooltip, setShowBoostTooltip] = useState(false);
  const [boostTooltipMessage, setBoostTooltipMessage] = useState('');
  const boostBtnRef = useRef<HTMLButtonElement>(null);

  const [isUtmLoading, setIsUtmLoading] = useState(false);
  const [showUtmModal, setShowUtmModal] = useState(false);
  const [utmApiResponse, setUtmApiResponse] = useState<{ final_url: string; utm_params: Record<string, string> } | null>(null);
  const [savedUtmResponse, setSavedUtmResponse] = useState<{ final_url: string; utm_params: Record<string, string> } | null>(null);
  
  // States for CreateSmartLinkModal
  const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);
  const [prefilledUrl, setPrefilledUrl] = useState<string>('');

  // Hooks for creating short links
  const createLinkMutation = useCreateLink();
  const { data: brandsData } = useBrands();

  const URL_DETECT_REGEX = /\b(?:https?:\/\/)?(?:www\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,6}(?:[\/?#][^\s]*)?\b/ig;

  const detectAndExtractUrl = (text: string) => {
    URL_DETECT_REGEX.lastIndex = 0;
    const match = URL_DETECT_REGEX.exec(text);
    if (match && match[0]) {
      setIsUrlDetected(true);
      setExtractedUrl(match[0]);
    } else {
      setIsUrlDetected(false);
      setExtractedUrl(null);
    }
  };

  useEffect(() => {
    setContent(initialContent);
    detectAndExtractUrl(initialContent);
  }, [initialContent]);



  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'draft':
        return { name: t('status.draft', 'Draft'), Icon: Edit3, color: 'gray', emoji: '📝' };
      case 'scheduled':
        return { name: t('status.scheduled', 'Scheduled'), Icon: Clock, color: 'blue', emoji: '🗓️' };
      case 'ready_to_publish':
        return { name: t('status.ready_to_publish', 'Ready to Publish'), Icon: Rocket, color: 'teal', emoji: '🚀' };
      case 'waiting_for_approval':
        return { name: t('status.waiting_for_approval', 'Needs Approval'), Icon: Eye, color: 'orange', emoji: '👀' };
      case 'published':
        return { name: t('status.published', 'Published'), Icon: CheckCircle, color: 'green', emoji: '✅' };
      case 'rejected':
        return { name: t('status.rejected', 'Rejected'), Icon: X, color: 'red', emoji: '❌' };
      case 'error':
         return { name: t('status.error', 'Error'), Icon: AlertTriangle, color: 'red', emoji: '⚠️' };
      case 'pending': 
        return { name: t('status.pending', 'Pending'), Icon: Clock, color: 'yellow', emoji: '🚧' };
      case 'waiting': 
        return { name: t('status.waiting', 'Waiting'), Icon: Clock, color: 'orange', emoji: '⏳' };
      default:
        return { name: status, Icon: AlertTriangle, color: 'gray', emoji: '❓' };
    }
  };

  const { name: statusName, emoji: statusEmoji, color: statusColor } = getStatusDisplay(status);

  const badgeBgClass = `bg-${statusColor}-100`;
  const badgeTextClass = `text-${statusColor}-700`;

  const isAiBoostDisabled = !brandId || !content.trim() || selectedPlatforms.length === 0 || isAiLoading;
  const isUtmBoostDisabled = !extractedUrl || selectedPlatforms.length === 0 || isUtmLoading;

  const getAiBoostDisabledReason = (): string => {
    if (isAiLoading) return '';
    if (!brandId) return t('tooltips.boostDisabled.selectBrand', 'Select a brand to enable AI Boost');
    if (!content.trim()) return t('tooltips.boostDisabled.addContent', 'Add content to enable AI Boost');
    if (selectedPlatforms.length === 0) return t('tooltips.boostDisabled.selectPlatform', 'Select at least one platform to enable AI Boost');
    return '';
  };

  const handleAiBoostMouseOver = () => {
    if (isAiBoostDisabled && !isAiLoading) {
      setBoostTooltipMessage(getAiBoostDisabledReason());
      setShowBoostTooltip(true);
    }
  };

  const handleAiBoostMouseOut = () => {
    setShowBoostTooltip(false);
    setBoostTooltipMessage('');
  };

  const handleBoostWithAi = async () => {
    if (isAiBoostDisabled) {
      console.warn('Boost with AI called while button should be disabled.');
      return;
    }

    setIsAiLoading(true);
    if (onAiResponseChange) {
      onAiResponseChange(null);
    }

    const user = auth.currentUser;
    if (!user) {
      alert(t('errors.authRequiredAi'));
      setIsAiLoading(false);
      return;
    }
    const token = await user.getIdToken();

    const API_ENDPOINT = 'https://api.socialness.ai/ai-coach/content/review'; 
    
    const requestPayload = {
      content: content,
      brandId: brandId,
      appLanguage: i18n.language || "en",
      platforms: selectedPlatforms
    };
    
    console.log('Sending AI Boost request to:', API_ENDPOINT);
    console.log('Payload:', requestPayload);
    
    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });
      
      console.log('Raw AI response status:', response.status);
      
      if (!response.ok) {
        let errorBody = 'No error details available.';
        try {
          errorBody = await response.text();
        } catch (parseError) {
          console.error('Could not parse error response body:', parseError);
        }
        console.error('API Error Body:', errorBody);
        let detail = '';
        try {
            const errorJson = JSON.parse(errorBody);
            detail = errorJson.detail || errorJson.message || '';
        } catch {}
        throw new Error(`API request failed: ${response.status}. ${detail || errorBody}`);
      }
      
      const data = await response.json();
      console.log('AI Response data:', data);
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response data structure');
      }
      
      const aiResponseData: AiBoostResponse = {
        hookStrength: data.hookStrength || 0,
        brandAlignment: data.brandAlignment || 0,
        ctaEffectiveness: data.ctaEffectiveness || 0,
        platformOptimization: data.platformOptimization || 0,
        contentQuality: data.contentQuality || 0,
        overallScore: data.overallScore || 0,
        originalScore: data.originalScore || {
          hookStrength: 0,
          brandAlignment: 0,
          ctaEffectiveness: 0,
          platformOptimization: 0,
          contentQuality: 0,
          overallScore: 0
        },
        feedback: data.feedback || '',
        enhancedContent: data.enhancedContent || '',
        enhancedScores: data.enhancedScores || {
          hookStrength: 0,
          brandAlignment: 0,
          ctaEffectiveness: 0,
          platformOptimization: 0,
          contentQuality: 0,
          overallScore: 0
        },
        reasoning: data.reasoning || '',
        originalContent: data.originalContent || content,
        brandId: data.brandId || brandId,
        platforms: data.platforms || selectedPlatforms,
        language: data.language || i18n.language || 'en',
        timestamp: data.timestamp || new Date().toISOString()
      };
      
      console.log('Structured AI response:', aiResponseData);
      if (onAiResponseChange) {
        onAiResponseChange(aiResponseData);
      }
      
    } catch (error) {
      console.error('Error boosting content with AI:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`${t('errors.boostFailed')}: ${errorMessage}`);
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleBoostLink = async () => {
    if (isUtmBoostDisabled || !extractedUrl) {
      alert(t('errors.utmBoostNotReady', 'Cannot boost link. Ensure a URL is present and a platform is selected.'));
      return;
    }

    setIsUtmLoading(true);
    setUtmApiResponse(null);

    const user = auth.currentUser;
    if (!user) {
      alert(t('errors.authRequiredAi', 'Authentication is required to boost links.'));
      setIsUtmLoading(false);
      return;
    }
    const token = await user.getIdToken();
    const API_ENDPOINT = 'https://api.socialness.ai/ai-coach/utm/generate';

    const platformForApi = selectedPlatforms[0]; 
    const formatForApi = format === 'reel' ? 'video' : 'image';

    const requestPayload = {
      postContent: content,
      platform: platformForApi,
      originalUrl: extractedUrl,
      format: formatForApi,
      language: i18n.language || "en",
    };

    console.log('Sending UTM Boost request to:', API_ENDPOINT);
    console.log('UTM Payload:', requestPayload);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });

      console.log('Raw UTM response status:', response.status);

      if (!response.ok) {
        let errorBody = 'No error details available.';
        try {
          errorBody = await response.text();
        } catch (parseError) {
          console.error('Could not parse UTM error response body:', parseError);
        }
        console.error('UTM API Error Body:', errorBody);
        let detail = '';
        try {
            const errorJson = JSON.parse(errorBody);
            detail = errorJson.detail || errorJson.message || '';
        } catch {}
        throw new Error(`UTM API request failed: ${response.status}. ${detail || errorBody}`);
      }

      const data = await response.json();
      console.log('UTM Response data:', data);

      if (!data || typeof data !== 'object' || !data.final_url || !data.utm_params) {
        throw new Error('Invalid UTM response data structure');
      }
      
      setUtmApiResponse(data);
      setSavedUtmResponse(data);
      setShowUtmModal(true);

    } catch (error) {
      console.error('Error boosting link with UTM:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`${t('errors.utmBoostFailed', 'Failed to boost link')}: ${errorMessage}`);
      setShowUtmModal(false);
      setUtmApiResponse(null);
    } finally {
      setIsUtmLoading(false);
    }
  };
  
  const handleUpdateLinkWithUtm = (newUrl: string) => {
    const updatedContent = content.replace(extractedUrl!, newUrl);
    setContent(updatedContent);
    onContentChange(updatedContent);
    setExtractedUrl(newUrl);
    setShowUtmModal(false);
    setUtmApiResponse(null);
  };

  const handleCreateShortLink = (utmUrl: string) => {
    // Store the UTM URL for prefilling the create link modal
    setPrefilledUrl(utmUrl);
    
    // Close UTM modal and open create link modal
    setShowUtmModal(false);
    setShowCreateLinkModal(true);
  };

  const handleGoBackToUtm = () => {
    console.log('handleGoBackToUtm called');
    console.log('utmApiResponse:', utmApiResponse);
    console.log('savedUtmResponse:', savedUtmResponse);
    
    // Close create link modal and reopen UTM modal with previous data
    setShowCreateLinkModal(false);
    setPrefilledUrl('');
    if (savedUtmResponse) {
      console.log('Restoring UTM modal with saved data');
      setUtmApiResponse(savedUtmResponse);
      setShowUtmModal(true);
    } else {
      console.log('No saved UTM response data available');
    }
  };

  const handleCreateSmartLink = async (data: { destinationUrl: string; shortCode: string; brandId?: string }) => {
    try {
      // Construct the short URL from the code
      const shortURL = `https://go.socialness.ai/${data.shortCode}`;
      
      // Map form data to API format
      const apiData = {
        oldURL: data.destinationUrl, // The URL with UTM parameters
        newURL: shortURL, // The new short URL
        brandId: data.brandId || ''
      };
      
      await createLinkMutation.mutateAsync(apiData);
      
      // Replace the original URL with the short URL in the content
      const updatedContent = content.replace(extractedUrl!, shortURL);
      setContent(updatedContent);
      onContentChange(updatedContent);
      setExtractedUrl(shortURL);
      
      // Close modal and reset states
      setShowCreateLinkModal(false);
      setPrefilledUrl('');
      setUtmApiResponse(null);
      setSavedUtmResponse(null);
      
      // Show success message
      alert(t('links.createSuccess', 'Short link created successfully!'));
      
    } catch (error) {
      console.error('Failed to create smart link:', error);
      alert(t('links.createError', 'Failed to create short link. Please try again.'));
    }
  };



  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900">{t('postContent.title', 'Post Content')}</h2>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeBgClass} ${badgeTextClass}`}>
            <span role="img" aria-label={statusName}>{statusEmoji}</span>
            {statusName}
          </span>
        </div>
        <div className="relative flex items-center gap-2">
          {isUrlDetected && (
            <button
              onClick={handleBoostLink}
              disabled={isUtmBoostDisabled}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-lg border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                isUtmLoading 
                ? 'bg-gray-100 text-gray-400 border-gray-300' 
                : 'text-primary-600 bg-white border-primary-600 hover:bg-primary-50'
              }`}
            >
              {isUtmLoading ? (
                <>
                  <div className="w-4 h-4 mr-1.5 border-2 border-t-transparent border-primary-600 rounded-full animate-spin"></div>
                  {t('common.trackingLink', 'Tracking Link...')}
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-1.5" />
                  {t('actions.trackYourLink', 'Track your link')}
                </>
              )}
            </button>
          )}
          <div className="relative">
            <button 
              ref={boostBtnRef}
              onClick={handleBoostWithAi}
              disabled={isAiBoostDisabled}
              onMouseOver={handleAiBoostMouseOver}
              onMouseOut={handleAiBoostMouseOut}
              onFocus={handleAiBoostMouseOver}
              onBlur={handleAiBoostMouseOut}
              className={`inline-flex items-center px-3 py-1.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${isAiBoostDisabled ? 'bg-gray-400 hover:bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'}`}
            >
              {isAiLoading ? (
                <>
                  <div className="w-4 h-4 mr-1.5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                  <span className="text-sm font-semibold">{t('common.boosting', 'Boosting...')}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  <span className="text-sm font-semibold">{t('actions.boostWithAi', 'Boost with AI')}</span>
                </>
              )}
            </button>
            {showBoostTooltip && isAiBoostDisabled && !isAiLoading && (
              <div 
                role="tooltip"
                className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-max max-w-xs px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md shadow-lg z-10 whitespace-nowrap"
              >
                <Info className="inline w-3 h-3 mr-1" /> {boostTooltipMessage}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center mb-1">
            <label htmlFor="caption" className="block text-sm font-medium text-gray-700">
              {t('postContent.caption', 'Caption')}
            </label>
            {isUrlDetected && (
              <span className="text-xs text-blue-700 font-medium">
                {t('postContent.urlDetected', 'URL Detected')}
              </span>
            )}
          </div>
          <textarea
            id="caption"
            value={content}
            onChange={(e) => {
              const newContent = e.target.value;
              setContent(newContent);
              onContentChange(newContent);
              detectAndExtractUrl(newContent);
            }}
            placeholder={t('postContent.placeholder', 'Write your post content here...')}
            className="w-full h-96 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-vertical min-h-[24rem]"
          />
        </div>
      </div>

      {showUtmModal && utmApiResponse && (
        <UTMModal
          isOpen={showUtmModal}
          onClose={() => {
            setShowUtmModal(false);
            setUtmApiResponse(null);
          }}
          onUpdateLink={handleUpdateLinkWithUtm}
          onCreateShortLink={handleCreateShortLink}
          utmResponse={utmApiResponse}
        />
      )}

      {showCreateLinkModal && (
        <CreateSmartLinkModal
          isOpen={showCreateLinkModal}
          onClose={() => {
            setShowCreateLinkModal(false);
            setPrefilledUrl('');
          }}
          onGoBack={handleGoBackToUtm}
          onSubmit={handleCreateSmartLink}
          brands={brandsData?.brands || []}
          isCreating={createLinkMutation.isPending}
          initialLinkData={null}
          isEditMode={false}
          prefilledUrl={prefilledUrl}
        />
      )}
    </div>
  );
};